import FileSaver from 'file-saver';
import { Flex, Icon, IconButton, useToast } from '@chakra-ui/react';
import ARetLangSelector from 'components/atoms/ARetLangSelector';
import { useLazyExportClientProductsQuery } from 'core/API/productsApi';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import { useTranslation } from 'helpers/languages/translations';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { tableToolbarStyle, iconTableStyle } from 'shared/styles';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { emptyProductTitle } from '../consts';
import { TProductTitleProps } from '../ProductTitleContainer';

const ProductTitleToolbar = ({
    retailer,
    lang,
    table,
    readonly,
    setLang,
    setRetailer,
    onTagOpen,
    onOpen,
    setModalType,
    setProductToUpdate,
}: TProductTitleProps) => {
    const PlusIcon = icons.plus;
    const DownloadIcon = icons.download;
    const ListIcon = icons.list;

    const translate = useTranslation();
    const toast = useToast();

    const [exportTitleSplit, { isLoading: isExportLoading }] = useLazyExportClientProductsQuery();
    const companyInfo = useGetCompanyInfoSelector();

    const { setGlobalFilter } = table;

    const handleAddProduct = () => {
        setModalType('add');
        setProductToUpdate(emptyProductTitle);
        onOpen();
    };

    const handleExportTitleSplit = async () => {
        try {
            const { data: res, isError: isExportError } = await exportTitleSplit({
                isTemplate: false,
                isTitleSplit: true,
                retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                lang,
            });
            if (isExportError || !res) throw new Error(translate('splits.export.error') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('splits.export.fileName', { name: companyInfo!.name_id }) as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    return (
        <Flex {...tableToolbarStyle} justifyContent="space-between">
            <Flex>
                <GlobalFilter globalFilter={table.getState().globalFilter} setGlobalFilter={setGlobalFilter} />
                <IconButton
                    {...iconTableStyle}
                    onClick={intentionalyFloatingPromiseReturn(handleExportTitleSplit)}
                    isLoading={isExportLoading}
                    aria-label="download excel"
                    size="sm"
                    icon={<DownloadIcon />}
                />
                <IconButton
                    onClick={handleAddProduct}
                    {...iconTableStyle}
                    aria-label="add alias"
                    size="sm"
                    icon={<Icon as={PlusIcon} />}
                    isDisabled={readonly}
                />
                <IconButton
                    onClick={onTagOpen}
                    {...iconTableStyle}
                    aria-label="tag split"
                    size="sm"
                    icon={<Icon as={ListIcon} />}
                />
            </Flex>
            <Flex>
                <ARetLangSelector
                    selectedRetailer={retailer}
                    selectedLang={lang}
                    setSelectedLang={setLang}
                    setSelectedRetailer={setRetailer}
                />
            </Flex>
        </Flex>
    );
};

export default ProductTitleToolbar;
